import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { usePermission } from '../../hooks/usePermission';
import { useListPageState } from '../../hooks/useListPageState';
import {
  useProjects,
  useProjectMilestoneNames,
  useProjectOutcomeNames,
} from '../../graphql/queries';
import {
  DataTable,
  StatusBadge,
  EmptyState,
  Pagination,
  PermissionGate,
  TableSkeleton,
  type DataTableColumn,
} from '../../components/shared';
import type { Project, ProjectFilter, ProjectStatus } from '../../graphql/types';
import '@tylertech/forge/text-field';
import '@tylertech/forge/select';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './index.css';

export const Route = createFileRoute('/projects/')({
  component: ProjectsListPage,
  validateSearch: (search: Record<string, unknown>) => ({
    page: Math.max(1, Number(search.page) || 1),
    pageSize: Number(search.pageSize) || 25,
  }),
});

export { ProjectsListPage };

interface ProjectFilters {
  search: string;
  status: ProjectStatus | '';
  milestoneName: string;
  outcomeName: string;
}

const ForgeButton = 'forge-button' as React.ElementType;
const ForgeSelect = 'forge-select' as React.ElementType;
const ForgeOption = 'forge-option' as React.ElementType;

function ProjectsListPage(): JSX.Element {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const { page, pageSize } = Route.useSearch();

  const [filters, setFilters] = useState<ProjectFilters>({
    search: '',
    status: '',
    milestoneName: '',
    outcomeName: '',
  });

  const canCreateProject = usePermission('project:create');

  // Milestone/outcome are server-side filters (bounded name -> projectId
  // catalog resolution, issue #646); search/status stay client-side over the
  // fetched batch, matching this page's existing pattern.
  const serverFilter: ProjectFilter | undefined = (() => {
    const f: ProjectFilter = {};
    if (filters.milestoneName) f.milestoneName = filters.milestoneName;
    if (filters.outcomeName) f.outcomeName = filters.outcomeName;
    return Object.keys(f).length > 0 ? f : undefined;
  })();

  const { data, loading, error } = useProjects(serverFilter);
  const { data: milestoneNamesData } = useProjectMilestoneNames();
  const { data: outcomeNamesData } = useProjectOutcomeNames();

  const projects = data?.projects?.edges?.map((edge) => edge.node) ?? [];

  const { sortColumn, sortDirection, handleSort, filteredItems, pagedItems, totalCount } =
    useListPageState<Project>({
      items: projects,
      page,
      pageSize,
      filterFn: (proj) => {
        if (filters.search) {
          const q = filters.search.toLowerCase();
          if (!proj.name.toLowerCase().includes(q) && !proj.description?.toLowerCase().includes(q))
            return false;
        }
        if (filters.status && proj.status !== filters.status) return false;
        return true;
      },
      sortValue: (proj, col) => String(proj[col as keyof Project] ?? ''),
    });

  const columns: DataTableColumn<Project>[] = [
    { key: 'name', label: 'Name', sortable: true, accessor: (p) => p.name },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (p) => <StatusBadge value={p.status} variant="lifecycle" />,
    },
    {
      key: 'ownerDivision',
      label: 'Division',
      sortable: true,
      accessor: (p) => p.ownerDivision,
    },
    { key: 'startDate', label: 'Start', sortable: true, accessor: (p) => p.startDate ?? '—' },
    {
      key: 'targetEndDate',
      label: 'Target End',
      sortable: true,
      accessor: (p) => p.targetEndDate ?? '—',
    },
  ];

  const handleRowClick = (proj: Project): void => {
    navigate({ to: '/projects/$projectId', params: { projectId: proj.entityId } });
  };

  if (loading) {
    return (
      <div className="projects-list-page">
        <TableSkeleton rows={8} columns={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="projects-list-page">
        <EmptyState
          icon="error"
          title="Failed to load projects"
          description={error.message}
          actionLabel="Retry"
          onAction={() => apolloClient.refetchQueries({ include: ['ListProjects'] })}
        />
      </div>
    );
  }

  return (
    <div className="projects-list-page">
      <div className="page-header page-header--toolbar page-header--spaced">
        <div className="page-header-title">
          <h1 className="forge-typography--headline4">Projects</h1>
          <span className="page-header-count forge-typography--body2">
            {totalCount} {totalCount === 1 ? 'project' : 'projects'}
          </span>
        </div>
        <div className="page-header-actions">
          <PermissionGate permission="project:create">
            <ForgeButton
              type="button"
              variant="raised"
              onClick={() => navigate({ to: '/projects/new' })}
            >
              New Project
            </ForgeButton>
          </PermissionGate>
        </div>
      </div>

      <div className="filters-row">
        <div className="search-field">
          <input
            type="text"
            className="search-input"
            placeholder="Search projects..."
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            aria-label="Search projects"
          />
        </div>

        <div className="filter-selects">
          <select
            className="filter-select"
            value={filters.status}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, status: e.target.value as ProjectStatus | '' }))
            }
            aria-label="Filter by status"
          >
            <option value="">All Statuses</option>
            <option value="planning">Planning</option>
            <option value="active">Active</option>
            <option value="on_hold">On Hold</option>
            <option value="complete">Complete</option>
            <option value="archived">Archived</option>
          </select>

          <ForgeSelect
            name="milestoneName"
            label="Milestone"
            value={filters.milestoneName}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setFilters((prev) => ({ ...prev, milestoneName: e.target.value }))
            }
          >
            <ForgeOption value="">All Milestones</ForgeOption>
            {[...(milestoneNamesData?.projectMilestoneNames ?? [])].map((name) => (
              <ForgeOption key={name} value={name}>
                {name}
              </ForgeOption>
            ))}
          </ForgeSelect>

          <ForgeSelect
            name="outcomeName"
            label="Outcome"
            value={filters.outcomeName}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setFilters((prev) => ({ ...prev, outcomeName: e.target.value }))
            }
          >
            <ForgeOption value="">All Outcomes</ForgeOption>
            {[...(outcomeNamesData?.projectOutcomeNames ?? [])].map((name) => (
              <ForgeOption key={name} value={name}>
                {name}
              </ForgeOption>
            ))}
          </ForgeSelect>
        </div>
      </div>

      {filteredItems.length === 0 ? (
        <EmptyState
          icon="folder_open"
          title="No projects found"
          description={
            filters.search || filters.status || filters.milestoneName || filters.outcomeName
              ? 'Try adjusting your filters'
              : 'Get started by creating your first project'
          }
          actionLabel={canCreateProject ? 'New Project' : undefined}
          onAction={canCreateProject ? () => navigate({ to: '/projects/new' }) : undefined}
        />
      ) : (
        <DataTable
          data={pagedItems}
          columns={columns}
          getRowKey={(p) => p.entityId}
          onRowClick={handleRowClick}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      )}

      <Pagination
        currentPage={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={(newPage) =>
          navigate({ to: '/projects', search: { page: newPage, pageSize } })
        }
        onPageSizeChange={(newSize) =>
          navigate({ to: '/projects', search: { page: 1, pageSize: newSize } })
        }
      />
    </div>
  );
}
